import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Icon from 'Components/Atoms/Icon/Icon';
import Spinner from 'Components/Atoms/Spinner';
import Button from 'Components/Atoms/Button/Button';
import { useSnackbar } from 'Components/Molecules/SnackbarProvider/SnackbarProvider';
import { useQueryClient } from '@tanstack/react-query';
import { deleteForecastValue } from 'Services/forecasts';
import { trackError } from 'Utils/errorMonitoring';
import warningIcon from 'assets/warning.svg';
import classes from './Forecast.module.css';
import ForecastValueCreate from './ForecastValueCreate';
import useForecastInspectValue from './useForecastInspectValue';

export default function ForecastValueGoal(props) {
    const {
        onClose,
        onCancel,
        cell,
        forecastId,
        periodicity,
        predictedOn,
        hasPeriodicityView,
        onRestorePeriodicity,
        isConsolidatedMatrix,
    } = props;

    const [processing, setProcessing] = useState(false);

    const { t } = useTranslation('forecasts');
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    const { forecastValue, isLoading } = useForecastInspectValue(forecastId, cell?.filters);

    const hasForecastValue = !!cell?.filters?.forecast_value_id;
    // The form is filled on mount, so it can not be rendered before the goal is there
    const isLoadingGoal = hasForecastValue && isLoading;
    const hasErrors = cell?.errors?.length > 0;

    const handleDeleteForecastValue = async () => {
        setProcessing(true);
        try {
            await deleteForecastValue(forecastId, cell?.filters?.forecast_value_id);
            await queryClient.invalidateQueries({ queryKey: ['forecasts', forecastId] });
        } catch (error) {
            trackError(error);
            enqueueSnackbar(t('error-deleting-forecast-value-title'), {
                variant: 'error',
                description: t('error-deleting-forecast-value-description'),
            });
        } finally {
            onClose();
            setProcessing(false);
        }
    };

    const ERROR_IDS = {
        invalid_reference_id: t('cell.invalid_reference_id'),
        circular_dependency: t('cell.circular_dependency'),
    };

    return (
        <div className="pt-2 min-h-full relative flex flex-col">
            <div className="mb-6 opacity-50 text-xs empty:hidden">{cell?.period_label}</div>

            {isConsolidatedMatrix && (
                <div className={classes.alert}>
                    <div className={classes.alertIcon}>
                        <img src={warningIcon} alt="" />
                    </div>
                    <div>
                        <div className={classes.alertTitle}>{t('warning-consolidated-matrix-title')}</div>
                        <div className={classes.alertText}>{t('warning-consolidated-matrix-description')}</div>
                    </div>
                </div>
            )}

            {!isConsolidatedMatrix && hasPeriodicityView && (
                <div className={classes.alert}>
                    <div className={classes.alertIcon}>
                        <img src={warningIcon} alt="" />
                    </div>
                    <div>
                        <div className="mb-4">
                            <div className={classes.alertTitle}>{t('warning-periodicity-title')}</div>
                            <div className={classes.alertText}>{t('warning-periodicity-description')}</div>
                        </div>

                        <Button className={classes.alerButton} size="small" onClick={onRestorePeriodicity}>
                            {t('restore')}
                        </Button>
                    </div>
                </div>
            )}

            {!isConsolidatedMatrix && !hasPeriodicityView && (
                <>
                    {hasErrors && (
                        <div>
                            <div className="pt-4 mb-2">
                                {cell.errors.map((error, index) => (
                                    <div key={index} className={clsx(classes.errorCard, 'mb-2')}>
                                        <Icon
                                            size={24}
                                            name="circle-alert"
                                            color="currentColor"
                                            className={classes.errorIcon}
                                        />
                                        <div className={classes.errorText}>{ERROR_IDS[error.type] || error.type}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="flex justify-center py-6">
                                <Button
                                    variant="primary"
                                    className={classes.dangerButton}
                                    onClick={handleDeleteForecastValue}
                                    isBusy={processing}
                                >
                                    {t('cell.remove-forecast-value')}
                                </Button>
                            </div>
                        </div>
                    )}

                    {!hasErrors && isLoadingGoal && (
                        <div className="flex justify-center py-6">
                            <Spinner />
                        </div>
                    )}

                    {!hasErrors && !isLoadingGoal && (
                        <ForecastValueCreate
                            cell={cell}
                            forecastId={forecastId}
                            periodicity={periodicity}
                            onClose={onClose}
                            onCancel={onCancel}
                            predictedOn={predictedOn}
                            forecastValue={hasForecastValue ? forecastValue : undefined}
                        />
                    )}
                </>
            )}
        </div>
    );
}

ForecastValueGoal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    forecastId: PropTypes.string.isRequired,
    onRestorePeriodicity: PropTypes.func.isRequired,
    cell: PropTypes.object,
    periodicity: PropTypes.string,
    predictedOn: PropTypes.string,
    hasPeriodicityView: PropTypes.bool,
    isConsolidatedMatrix: PropTypes.bool,
};

ForecastValueGoal.drawerStackProps = {
    size: 'lg',
};
